/**
 * Base-level Purchase Orders settings (letterhead + signatures).
 * Stored on `_bases/{baseKey}.purchaseOrders`.
 */

export const DEFAULT_COMPANY_SIGNATURE_TITLE = 'Company Signature';
export const DEFAULT_VENDOR_SIGNATURE_TITLE = 'Vendor Signature';
export const SIGNATURE_SHOW_AS_TEXT = 'text';
export const SIGNATURE_SHOW_AS_WRITTEN = 'written';

function normalizeString(value) {
  return String(value || '').trim();
}

function normalizeSignatureShowAs(value) {
  const normalized = normalizeString(value).toLowerCase();
  return normalized === SIGNATURE_SHOW_AS_WRITTEN
    ? SIGNATURE_SHOW_AS_WRITTEN
    : SIGNATURE_SHOW_AS_TEXT;
}

function normalizeAddress(value) {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : null;
}

/**
 * @param {object|null|undefined} raw
 * @returns {{
 *   phone: string,
 *   address: object|null,
 *   companySignatureTitle: string,
 *   vendorSignatureTitle: string,
 *   signatureSubtitle: string,
 *   signatureShowAs: 'text'|'written',
 * }}
 */
export function normalizePurchaseOrderSettings(raw) {
  const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  return {
    phone: normalizeString(source.phone),
    address: normalizeAddress(source.address),
    companySignatureTitle: (
      normalizeString(source.companySignatureTitle) || DEFAULT_COMPANY_SIGNATURE_TITLE
    ),
    vendorSignatureTitle: (
      normalizeString(source.vendorSignatureTitle) || DEFAULT_VENDOR_SIGNATURE_TITLE
    ),
    signatureSubtitle: normalizeString(source.signatureSubtitle),
    signatureShowAs: normalizeSignatureShowAs(source.signatureShowAs),
  };
}

/**
 * @param {object|null|undefined} base
 * @returns {{
 *   phone: string,
 *   address: object|null,
 *   companySignatureTitle: string,
 *   vendorSignatureTitle: string,
 *   signatureSubtitle: string,
 *   signatureShowAs: 'text'|'written',
 * }}
 */
export function getPurchaseOrderSettings(base) {
  return normalizePurchaseOrderSettings(base?.purchaseOrders);
}

/**
 * Builds a signatures-only `purchaseOrders` patch. Letterhead phone/address
 * live on `base.documents` (see documentBaseSettings.js).
 *
 * @param {object} next
 * @param {object|null|undefined} [existingRaw] - Current `base.purchaseOrders`
 * @returns {{ purchaseOrders: object }}
 */
export function buildPurchaseOrderSettingsPatch(next = {}, existingRaw = null) {
  const existing = normalizePurchaseOrderSettings(existingRaw);
  const merged = {
    // Preserve legacy letterhead keys if present so older readers keep working
    // until Documents settings are saved; do not overwrite from this pane.
    phone: existing.phone,
    address: existing.address,
    companySignatureTitle: next.companySignatureTitle !== undefined
      ? (normalizeString(next.companySignatureTitle) || DEFAULT_COMPANY_SIGNATURE_TITLE)
      : existing.companySignatureTitle,
    vendorSignatureTitle: next.vendorSignatureTitle !== undefined
      ? (normalizeString(next.vendorSignatureTitle) || DEFAULT_VENDOR_SIGNATURE_TITLE)
      : existing.vendorSignatureTitle,
    signatureSubtitle: next.signatureSubtitle !== undefined
      ? normalizeString(next.signatureSubtitle)
      : existing.signatureSubtitle,
    signatureShowAs: next.signatureShowAs !== undefined
      ? normalizeSignatureShowAs(next.signatureShowAs)
      : existing.signatureShowAs,
  };

  return { purchaseOrders: merged };
}

/**
 * @param {object} a
 * @param {object} b
 * @returns {boolean}
 */
export function purchaseOrderSettingsEqual(a, b) {
  const left = normalizePurchaseOrderSettings(a);
  const right = normalizePurchaseOrderSettings(b);
  if (left.companySignatureTitle !== right.companySignatureTitle) return false;
  if (left.vendorSignatureTitle !== right.vendorSignatureTitle) return false;
  if (left.signatureSubtitle !== right.signatureSubtitle) return false;
  if (left.signatureShowAs !== right.signatureShowAs) return false;
  return true;
}
